import { useEffect, useRef } from 'react';

import { Permission, planLinkReasonFor, subscriptionPlanPath } from '@bookora/shared';
import { useQueryClient } from '@tanstack/react-query';
import { App as AntdApp, Button } from '@bookora/ui';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/token-store';
import { settingsKeys } from '@/features/settings/hooks';
import { planLockReason } from '@/lib/reasons';
import { useCan } from '@/permissions/use-can';

import { invalidatePlanAccess } from './plan-access';
import { COMPARE_PLANS_LABEL, REFUSAL_TITLE, UNKNOWN_PLAN_NAME } from './plan-lock-copy';
import { PLAN_LOCK_NOTIFICATION_KEY, planRefusalOf } from './plan-refusal';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  THE 402 FEATURE_NOT_IN_PLAN WATCHER — and keeping plan access fresh
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Mounted once in the shell beside `BillingRefusalWatcher`. Three jobs:
 *
 *  1. **Explain a refused write.** It listens to the MUTATION cache, so every
 *     save in every feature is covered without the feature knowing about
 *     plans — a row edit in a read-only section, a stale tab whose plan was
 *     downgraded a minute ago. The first refusal raises ONE persistent
 *     notification «این کار در پلن فعلی نیست» with `planLockReason()` (the
 *     plan that has it, from the server's `upgradePlanName`); a
 *     `settings.billing` holder also gets «مقایسهٔ پلن‌ها» to that plan's card
 *     (`upgradePlanKey`, `for=<feature>`). A read-only 402 is ignored — the
 *     billing watcher owns it (`plan-refusal.ts`).
 *  2. **Re-read plan access after a refusal.** The server just said the plan
 *     differs from what the panel drew; the locks catch up.
 *  3. **Re-read it after a plan change.** Subscribe, cancel, resume, the
 *     scheduled-change cancel and the trial-plan switch all refresh the
 *     billing state; when that state's subscription changes, plan access is
 *     invalidated too — so the subscription screens need no knowledge of the
 *     lock family. So does the Zarinpal return (`?payment=paid`).
 */
export function PlanRefusalWatcher(): null {
  const queryClient = useQueryClient();
  const { notification } = AntdApp.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeClinicId } = useAuth();
  const canBilling = useCan(Permission.SETTINGS_BILLING);

  // 1 + 2 — refused writes.
  useEffect(() => {
    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'error') return;
      const refusal = planRefusalOf(event.action.error);
      if (!refusal) return;

      void invalidatePlanAccess(queryClient, activeClinicId);

      const planName = refusal.upgradePlanName ?? UNKNOWN_PLAN_NAME;
      const to = subscriptionPlanPath(
        refusal.upgradePlanKey,
        refusal.feature ? planLinkReasonFor(refusal.feature) : null,
      );
      notification.warning({
        key: PLAN_LOCK_NOTIFICATION_KEY,
        // Persistent: the screen's own toast is the fading half of this.
        duration: 0,
        title: REFUSAL_TITLE,
        description: planLockReason(planName, canBilling),
        actions: canBilling ? (
          <Button
            type="primary"
            size="small"
            onClick={() => {
              notification.destroy(PLAN_LOCK_NOTIFICATION_KEY);
              void navigate(to);
            }}
          >
            {COMPARE_PLANS_LABEL}
          </Button>
        ) : null,
      });
    });
  }, [queryClient, notification, navigate, activeClinicId, canBilling]);

  // 3 — a plan change, seen through the billing state it refreshes.
  const lastSubscription = useRef<string | null>(null);
  useEffect(() => {
    lastSubscription.current = null;
    const billingKey = JSON.stringify(settingsKeys.billing(activeClinicId));
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      if (JSON.stringify(event.query.queryKey) !== billingKey) return;
      const state = event.query.state.data as
        { subscription?: unknown; scheduledSubscription?: unknown } | undefined;
      const fingerprint = JSON.stringify([
        state?.subscription ?? null,
        state?.scheduledSubscription ?? null,
      ]);
      const previous = lastSubscription.current;
      lastSubscription.current = fingerprint;
      if (previous !== null && previous !== fingerprint) {
        void invalidatePlanAccess(queryClient, activeClinicId);
      }
    });
  }, [queryClient, activeClinicId]);

  // 3 — back from the gateway with a paid invoice.
  const paid = new URLSearchParams(location.search).get('payment') === 'paid';
  useEffect(() => {
    if (paid) void invalidatePlanAccess(queryClient, activeClinicId);
  }, [paid, location.key, queryClient, activeClinicId]);

  return null;
}
